import { useEffect, useState } from 'react';
import { AlertTriangle, CheckCircle2, Info, X } from 'lucide-react';
import { formatApiError } from '@/lib/utils';

/* ─── API pública — usar en cualquier archivo ─────────────────────
   import { showAlert } from '@/components/ui/GlobalAlert';
   showAlert('Mensaje de error');
   showAlert('Guardado correctamente', 'success');
   showAlert('¿Seguro?', 'warning');
──────────────────────────────────────────────────────────────────── */
const EVENT = 'lexorbit:alert';

export function showAlert(message, type = 'error') {
  // Si llega un `detail` de la API (puede ser array/objeto ante un 422), lo
  // pasamos a string para no romper el render del mensaje.
  const safe = typeof message === 'string' ? message : formatApiError(message);
  window.dispatchEvent(new CustomEvent(EVENT, { detail: { message: safe, type } }));
}

const ICONS = {
  error:   <AlertTriangle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />,
  warning: <AlertTriangle className="w-5 h-5 text-[#C9A44C] flex-shrink-0 mt-0.5" />,
  success: <CheckCircle2  className="w-5 h-5 text-green-400 flex-shrink-0 mt-0.5" />,
  info:    <Info          className="w-5 h-5 text-blue-400  flex-shrink-0 mt-0.5" />,
};

const BORDERS = {
  error:   'border-red-500/30',
  warning: 'border-[#C9A44C]/30',
  success: 'border-green-500/30',
  info:    'border-blue-500/30',
};

export default function GlobalAlert() {
  const [queue, setQueue] = useState([]);

  useEffect(() => {
    const handler = (e) => {
      const id = Date.now() + Math.random();
      setQueue((q) => [...q, { id, ...e.detail }]);
    };
    window.addEventListener(EVENT, handler);
    return () => window.removeEventListener(EVENT, handler);
  }, []);

  const cerrar = (id) => setQueue((q) => q.filter((a) => a.id !== id));

  if (queue.length === 0) return null;

  const { id, message, type } = queue[0];

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
      <div
        className={`relative w-full max-w-sm bg-[#0E0E0E] border ${BORDERS[type] || BORDERS.error} rounded-2xl shadow-2xl p-6`}
        style={{ fontFamily: 'Outfit, sans-serif' }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Línea superior decorativa */}
        <div className="absolute top-0 left-6 right-6 h-[1px] bg-gradient-to-r from-transparent via-[#C9A44C]/40 to-transparent rounded-full" />

        <div className="flex gap-3">
          {ICONS[type] || ICONS.error}
          <p className="text-sm text-[#E8E6E0] leading-relaxed">{message}</p>
        </div>

        <button
          onClick={() => cerrar(id)}
          className="mt-5 w-full py-2.5 rounded-xl bg-[#C9A44C]/15 border border-[#C9A44C]/25 text-[#C9A44C] text-sm font-medium hover:bg-[#C9A44C]/25 transition-all"
        >
          Aceptar
        </button>

        {/* Si hay más mensajes en cola, mostrá el contador */}
        {queue.length > 1 && (
          <p className="text-center text-[10px] text-[#555] mt-2">{queue.length - 1} más</p>
        )}
      </div>
    </div>
  );
}
